export interface AzureCostDailyPoint {
  date: string;
  dailyCost: number;
  accumulatedCost: number;
  isForecast: boolean;
}

export interface AzureCostByService {
  serviceName: string;
  cost: number;
}

export interface AzureEngagementCostResult {
  engagementId: string;
  engagementNumber: string;
  engagementName: string;
  subscriptionId: string;
  currency: string;
  actualCostMtd: number;
  forecastMonthEnd: number | null;
  dailySeries: AzureCostDailyPoint[];
  byService: AzureCostByService[];
  error?: string;
}

export interface AzureSkippedEngagement {
  engagementId: string;
  engagementNumber: string;
  engagementName: string;
  reason: string;
}

export interface AzureMonthlyTotal {
  year: number;
  month: number;
  label: string;
  totalCost: number;
}

export interface AzureServiceCostDelta {
  serviceName: string;
  previousCost: number;
  currentCost: number;
  delta: number;
}

export interface AzureServiceMonthColumn {
  year: number;
  month: number;
  label: string;
  meterCount: number;
  cost: number;
}

export interface AzureService3MonthAnalysisRow {
  serviceName: string;
  months: AzureServiceMonthColumn[];
  costDiff: number;
  trend: 'increased' | 'decreased' | 'unchanged';
}

export interface AzureCustomerCostResult {
  customerId: string;
  customerName: string;
  currency: string;
  actualCostMtd: number;
  forecastMonthEnd: number | null;
  dailySeries: AzureCostDailyPoint[];
  byService: AzureCostByService[];
  last3Months: AzureMonthlyTotal[];
  serviceCostDeltas: AzureServiceCostDelta[];
  serviceAnalysis3Months: AzureService3MonthAnalysisRow[];
  engagements: AzureEngagementCostResult[];
  skippedEngagements?: AzureSkippedEngagement[];
}

export interface AzureCostCustomerBase {
  customerId: string;
  customerName: string;
  currency: string;
}

export interface AzureSubscriptionCostResult extends AzureCostCustomerBase {
  actualCostMtd: number;
  forecastMonthEnd: number | null;
  dailySeries: AzureCostDailyPoint[];
  byService: AzureCostByService[];
  engagements: AzureEngagementCostResult[];
  skippedEngagements?: AzureSkippedEngagement[];
}

/** 3-month bar chart, observations, and analysis table (single API). */
export interface AzureLast3MonthsAnalysisResult extends AzureCostCustomerBase {
  last3Months: AzureMonthlyTotal[];
  serviceCostDeltas: AzureServiceCostDelta[];
  serviceAnalysis3Months: AzureService3MonthAnalysisRow[];
}

export function mergeAzureCostSections(
  subscription?: AzureSubscriptionCostResult,
  analysis?: AzureLast3MonthsAnalysisResult
): AzureCustomerCostResult | undefined {
  const base = subscription ?? analysis;
  if (!base) return undefined;

  return {
    customerId: base.customerId,
    customerName: base.customerName,
    currency: base.currency,
    actualCostMtd: subscription?.actualCostMtd ?? 0,
    forecastMonthEnd: subscription?.forecastMonthEnd ?? null,
    dailySeries: subscription?.dailySeries ?? [],
    byService: subscription?.byService ?? [],
    last3Months: analysis?.last3Months ?? [],
    serviceCostDeltas: analysis?.serviceCostDeltas ?? [],
    serviceAnalysis3Months: analysis?.serviceAnalysis3Months ?? [],
    engagements: subscription?.engagements ?? [],
    skippedEngagements: subscription?.skippedEngagements,
  };
}

export interface AzureCostApiResponse {
  success: boolean;
  data?: AzureCustomerCostResult;
  message?: string;
}
